import { FC } from "react";
import { useRouter } from "expo-router";
import { useAtom, useAtomValue } from "jotai/react";

import { VnBox } from "@/components/VnBox";
import { VnStepIndicator } from "@/components/VnStepIndicator";
import { VnText } from "@/components/VnText/VnText";
import { I18nKeyType } from "@/i18n";
import {
  applicationCurrentStepAtom,
  applicationTotalStepsAtom,
} from "@/stores/atoms";
import { cn } from "@/utils/tailwind";

import { BottomNavigator } from "./BottomNavigator";
import { ScreenLayout, ScreenLayoutPropsType } from "./ScreenLayout";

type ApplicationScreenLayoutPropsType = Pick<
  ScreenLayoutPropsType,
  "variant"
> & {
  children: React.ReactNode;
  shouldShowBackButton?: boolean;
  shouldShowNextButton?: boolean;
  onBackButtonPress?: () => void;
  onNextButtonPress?: () => void;
  className?: string;
  isNextButtonLoading?: boolean;
  isNextButtonDisabled?: boolean;
  nextButtonI18nKey?: I18nKeyType;
  titleI18nKey?: I18nKeyType;
  descriptionI18nKey?: I18nKeyType;
  titleI18nOptions?: Record<string, unknown>;
  descriptionI18nOptions?: Record<string, unknown>;
};

export const ApplicationScreenLayout: FC<ApplicationScreenLayoutPropsType> = ({
  children,
  shouldShowBackButton = true,
  shouldShowNextButton = true,
  onBackButtonPress,
  onNextButtonPress,
  variant,
  className,
  isNextButtonLoading,
  isNextButtonDisabled,
  nextButtonI18nKey,
  titleI18nKey,
  descriptionI18nKey,
  titleI18nOptions,
  descriptionI18nOptions,
}) => {
  const router = useRouter();
  const [applicationFlowCurrentStep, setApplicationCurrentStep] = useAtom(
    applicationCurrentStepAtom,
  );
  const applicationTotalSteps = useAtomValue(applicationTotalStepsAtom);

  const handleBackButtonPress = () => {
    setApplicationCurrentStep(step => Math.max(step - 1, 1));
    router.back();
  };

  return (
    <VnBox className="bg-background h-full">
      <ScreenLayout
        variant={variant}
        className="mx-auto w-full max-w-lg gap-6 pb-6 pt-6"
        backgroundClassName="bg-background">
        <VnStepIndicator
          currentStep={applicationFlowCurrentStep}
          totalSteps={applicationTotalSteps}
          titleVariant="lgBold"
          titleClassName="self-center"
          className="mb-4"
        />
        <VnBox className={cn("gap-6 px-2", className)}>
          {titleI18nKey && (
            <VnBox className="gap-2">
              <VnText
                variant="headerTwo"
                i18nKey={titleI18nKey}
                i18nOptions={titleI18nOptions}
              />

              {descriptionI18nKey && (
                <VnText
                  variant="lg"
                  className="text-muted-foreground"
                  i18nKey={descriptionI18nKey}
                  i18nOptions={descriptionI18nOptions}
                />
              )}
            </VnBox>
          )}

          {children}
        </VnBox>
      </ScreenLayout>
      <BottomNavigator
        shouldShowBackButton={shouldShowBackButton}
        shouldShowNextButton={shouldShowNextButton}
        onBackButtonPress={onBackButtonPress ?? handleBackButtonPress}
        onNextButtonPress={onNextButtonPress}
        isNextButtonLoading={isNextButtonLoading}
        isNextButtonDisabled={isNextButtonDisabled}
        nextButtonI18nKey={nextButtonI18nKey}
      />
    </VnBox>
  );
};
